import type { HasilZaka, JariMasukan, LobusKey } from "@dfa/types";
import { VEKTOR_RUMUS } from "../../lib/rumusZaka.js";
import {
  KETELITIAN_DEFAULT,
  URUTAN_KECERDASAN,
  kelompokMi,
  type Ketelitian,
} from "./ketelitian.js";

const LOBUS_ROWS: { key: LobusKey; label: string; side: "Kiri" | "Kanan" }[] = [
  { key: "KPre", label: "Prefrontal", side: "Kiri" },
  { key: "KFro", label: "Frontal", side: "Kiri" },
  { key: "KPar", label: "Parietal", side: "Kiri" },
  { key: "KTem", label: "Temporal", side: "Kiri" },
  { key: "KOcc", label: "Occipital", side: "Kiri" },
  { key: "NPre", label: "Prefrontal", side: "Kanan" },
  { key: "NFro", label: "Frontal", side: "Kanan" },
  { key: "NPar", label: "Parietal", side: "Kanan" },
  { key: "NTem", label: "Temporal", side: "Kanan" },
  { key: "NOcc", label: "Occipital", side: "Kanan" },
];

const KAP_ORDER: (keyof HasilZaka["kapasitas"])[] = [
  "Volition",
  "Memory",
  "Judment",
  "Conceptualization",
  "Intuition",
  "Creativity",
  "Comprehension",
  "Reasoning",
  "Association",
];

const KODE_KE_LOBUS: Record<string, LobusKey> = {
  R1: "KPre",
  R2: "KFro",
  R3: "KPar",
  R4: "KTem",
  R5: "KOcc",
  L1: "NPre",
  L2: "NFro",
  L3: "NPar",
  L4: "NTem",
  L5: "NOcc",
};

function fmt(n: number, d = 1): string {
  return n.toFixed(d);
}

/** Catatan ketelitian per kartu — angka dari Ketelitian, bukan teks tetap. */
export function catatanPetaLobus(ketelitian: Ketelitian): string {
  return (
    `Kalau dipindai ulang, angka pada peta ini bergeser sekitar ${fmt(ketelitian.pitaLobusPP, 1)} poin persen ` +
    `(galat pembacaan sulur MAE ${fmt(ketelitian.maeRidge, 2)}). Alat ukurnya belum cukup teliti untuk mengurutkan ` +
    `lobus yang berdekatan — selisih di bawah pita itu tidak dibedakan.`
  );
}

export function catatanRasional(ketelitian: Ketelitian): string {
  return (
    `Kalau dipindai ulang, pecahan ini bergeser sekitar ${fmt(ketelitian.pitaLobusPP, 1)} poin persen. ` +
    `Alat ukurnya belum cukup teliti untuk mengklaim sisi mana yang "lebih kuat" bila selisihnya tipis.`
  );
}

export function catatanKapasitas(ketelitian: Ketelitian): string {
  return (
    `Kalau dipindai ulang, angka kapasitas bergeser mengikuti galat sulur (MAE ${fmt(ketelitian.maeRidge, 2)}). ` +
    `Alat ukurnya belum cukup teliti untuk mengurutkan kapasitas yang berdekatan.`
  );
}

export function catatanKecerdasan(ketelitian: Ketelitian, namaDepan: string): string {
  return (
    `Jarak antar-kecerdasan di dalam satu kelompok lebih kecil daripada ketelitian alat ukur kami ` +
    `(pita kelompok ±${fmt(ketelitian.pitaMiPP, 2)} poin persen). Kalau ${namaDepan} dipindai ulang, ` +
    `bukan hanya urutannya yang bisa bertukar — yang masuk kelompok ini pun bisa berganti. ` +
    `Karena itu bacalah ketiganya sebagai satu gambaran, bukan sebagai tiga nama yang pasti.`
  );
}

export function catatanGayaBelajar(ketelitian: Ketelitian): string {
  return (
    `Kalau dipindai ulang, ketiga persen ini bergeser sekitar ${fmt(ketelitian.pitaLobusPP, 1)} poin. ` +
    `Alat ukurnya belum cukup teliti untuk mengklaim gaya mana yang mutlak dominan bila selisihnya kecil.`
  );
}

export function catatanGayaBerfikir(ketelitian: Ketelitian): string {
  return (
    `Kalau dipindai ulang, segmen ini bergeser sekitar ${fmt(ketelitian.pitaLobusPP, 1)} poin persen. ` +
    `Alat ukurnya belum cukup teliti untuk mengurutkan gaya yang berdekatan.`
  );
}

function CatatanKetelitian({ children }: { children: string }) {
  return (
    <p className="text-xs text-slate-600 border-t border-slate-100 pt-3 leading-relaxed" data-ketelitian="">
      {children}
    </p>
  );
}

function KapBar({
  nama,
  item,
}: {
  nama: string;
  item: HasilZaka["kapasitas"][keyof HasilZaka["kapasitas"]];
}) {
  if (item.bobotTerukur === 0 || item.nilai === null) {
    return (
      <div className="grid grid-cols-[150px_1fr] gap-3 items-center text-sm py-1">
        <span className="font-medium text-slate-800">{nama}</span>
        <div className="text-slate-600 text-xs border border-dashed border-slate-300 rounded px-3 py-2">
          <span className="font-mono font-bold">X</span> — tidak bisa dihitung: seluruh masukannya dari
          jari yang polanya arch
        </div>
      </div>
    );
  }
  const max = 25;
  const pct = Math.min(100, (item.nilai / max) * 100);
  return (
    <div className="grid grid-cols-[150px_1fr_52px] gap-3 items-center text-sm py-1">
      <span className="font-medium text-slate-800">{nama}</span>
      {item.bandingkan ? (
        <>
          <div className="h-4 bg-slate-100 rounded overflow-hidden">
            <div className="h-full bg-emerald-700 rounded-r" style={{ width: `${pct}%` }} />
          </div>
          <span className="font-mono text-right">{fmt(item.nilai, 1)}</span>
        </>
      ) : (
        <div className="col-span-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          {fmt(item.nilai, 1)} — sebagian masukannya tidak terukur (di luar grafik banding)
        </div>
      )}
    </div>
  );
}

function MiRow({
  nama,
  item,
}: {
  nama: string;
  item: HasilZaka["kecerdasan"][keyof HasilZaka["kecerdasan"]];
}) {
  if (item.simpangan === null || item.pangsa === null) {
    return (
      <div className="grid grid-cols-[168px_1fr_72px] gap-3 items-center text-sm py-1">
        <span>{nama}</span>
        <div className="text-xs text-slate-600 border border-dashed border-slate-300 rounded px-2 py-1">
          <span className="font-mono font-bold">X</span> — tidak bisa dibandingkan (masukan arch)
        </div>
        <span />
      </div>
    );
  }
  const half = 40;
  const width = Math.min(half, Math.abs((item.simpangan / 12.5) * half));
  const left = item.simpangan >= 0 ? half : half - width;
  return (
    <div className="grid grid-cols-[168px_1fr_72px] gap-3 items-center text-sm py-1">
      <span>{nama}</span>
      <div className="relative h-6 bg-slate-100 rounded">
        <div className="absolute top-0 bottom-0 left-1/2 w-px bg-slate-400 opacity-60" />
        <div
          className={`absolute top-1 h-4 rounded ${item.simpangan >= 0 ? "bg-emerald-600" : "bg-violet-600"}`}
          style={{ left: `${left}%`, width: `${width}%` }}
        />
      </div>
      <span className="font-mono text-right">{item.simpangan >= 0 ? "+" : ""}{fmt(item.simpangan, 2)}</span>
    </div>
  );
}

function DataSumber({
  hasil,
  jari,
  ketelitian,
}: {
  hasil: HasilZaka;
  jari: JariMasukan[];
  ketelitian: Ketelitian;
}) {
  return (
    <section className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
      <h2 className="text-lg font-semibold text-slate-900">Data Sumber</h2>
      <p className="text-sm text-slate-600">
        Semua angka di halaman ini berasal dari sepuluh baris berikut. Tidak ada yang ditambahkan di
        tengah jalan.
      </p>
      <div className="overflow-x-auto scrollx">
        <table className="w-full text-sm border-collapse min-w-[480px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3 font-mono">Kode</th>
              <th className="py-2 pr-3">Pola</th>
              <th className="py-2 pr-3 text-right">Hitungan sulur</th>
              <th className="py-2">Mengisi area</th>
            </tr>
          </thead>
          <tbody>
            {jari.map((j) => {
              const slot = KODE_KE_LOBUS[j.kode];
              const row = LOBUS_ROWS.find((r) => r.key === slot);
              const x = hasil.jariX.includes(j.kode);
              const sulur = x ? "X" : String(slot ? hasil.lobus_nilai[slot] : "—");
              return (
                <tr key={j.kode} className="border-b border-slate-100">
                  <td className="py-2 pr-3 font-mono font-medium">{j.kode}</td>
                  <td className="py-2 pr-3">{j.pola}</td>
                  <td className="py-2 pr-3 text-right font-mono">{sulur}</td>
                  <td className="py-2">
                    {row ? `${row.side} · ${row.label}` : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <CatatanKetelitian>
        {`Ketelitian pembacaan sulur: MAE ${fmt(ketelitian.maeRidge, 2)}. Jari berpola arch ditandai X — tidak punya titik delta, bukan nol.`}
      </CatatanKetelitian>
    </section>
  );
}

export function LaporanReport({
  hasil,
  meta,
  ketelitian = KETELITIAN_DEFAULT,
  jari,
}: {
  hasil: HasilZaka;
  meta: { nama: string; usia: string; kelamin: "L" | "P"; tanggal: string; kode: string };
  ketelitian?: Ketelitian;
  jari?: JariMasukan[];
}) {
  const kapSorted = [...KAP_ORDER].sort(
    (a, b) => (hasil.kapasitas[b].nilai ?? -1) - (hasil.kapasitas[a].nilai ?? -1),
  );

  const miByGroup = {
    menonjol: [] as string[],
    tengah: [] as string[],
    "kurang menonjol": [] as string[],
  };
  for (const nama of URUTAN_KECERDASAN) {
    const k = kelompokMi(hasil.kecerdasan[nama].simpangan, ketelitian.pitaMiPP);
    if (k) miByGroup[k].push(nama);
  }

  const jariData =
    jari ??
    VEKTOR_RUMUS.find((k) => k.nama === meta.kode || (meta.kode === "kirana-mockup" && k.nama === "kirana-mockup"))
      ?.masukan;

  const namaDepan = meta.nama.split(" ")[0];

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-6 bg-[#F3F2EA] min-h-screen text-slate-700">
      {/* §0 Kepala */}
      <header className="bg-white border border-slate-200 rounded-lg p-6 grid md:grid-cols-[1fr_auto] gap-6">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Laporan potensi</p>
          <h1 className="text-2xl font-semibold text-slate-900 mt-1">{meta.nama}</h1>
          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-sm text-slate-500">
            <span>Usia <b className="text-slate-800">{meta.usia}</b></span>
            <span>Kelamin <b className="text-slate-800">{meta.kelamin === "P" ? "Perempuan" : "Laki-laki"}</b></span>
            <span>Tanggal <b className="text-slate-800">{meta.tanggal}</b></span>
            <span>Kode <b className="text-slate-800 font-mono">{meta.kode}</b></span>
          </div>
        </div>
        <div className="text-right border-l border-slate-200 pl-6">
          <p className="text-xs uppercase text-slate-500">TNGF</p>
          <p className="text-4xl font-semibold text-slate-900 tabular-nums">
            {hasil.TNGF}
            <span className="text-lg text-slate-500 font-normal"> ± {Math.round(ketelitian.pitaTngf)}</span>
          </p>
        </div>
      </header>

      {/* §1 Peta lobus */}
      <section className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
        <h2 className="text-lg font-semibold text-slate-900">Peta Sebaran Saraf Otak</h2>
        <div className="overflow-x-auto scrollx">
          <div className="min-w-[520px] space-y-2">
            {["Prefrontal", "Frontal", "Parietal", "Temporal", "Occipital"].map((label) => {
              const k = LOBUS_ROWS.find((r) => r.label === label && r.side === "Kiri")!;
              const n = LOBUS_ROWS.find((r) => r.label === label && r.side === "Kanan")!;
              const pk = hasil.lobus_persen[k.key];
              const pn = hasil.lobus_persen[n.key];
              const xk = hasil.lobus_X.includes(k.key);
              const xn = hasil.lobus_X.includes(n.key);
              return (
                <div key={label} className="grid grid-cols-[1fr_100px_1fr] gap-2 items-center text-sm">
                  <div className="flex items-center gap-2 justify-end">
                    <span className="font-mono text-xs text-slate-500 w-12 text-right">
                      {xk ? "X" : `${fmt(pk, 1)}%`}
                    </span>
                    <div className="h-4 bg-emerald-700 rounded-l" style={{ width: `${Math.min(40, pk)}%`, minWidth: xk ? 0 : 4 }} />
                  </div>
                  <div className="text-center text-slate-800 font-medium">{label}</div>
                  <div className="flex items-center gap-2">
                    <div className="h-4 bg-sky-700 rounded-r" style={{ width: `${Math.min(40, pn)}%`, minWidth: xn ? 0 : 4 }} />
                    <span className="font-mono text-xs text-slate-500 w-12">
                      {xn ? "X" : `${fmt(pn, 1)}%`}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {hasil.jariX.length > 0 && (
          <p className="text-xs text-slate-600">
            Jari {hasil.jariX.join(", ")} berpola arch — slot lobus ditandai <b>X</b>, bukan 0%.
          </p>
        )}
        <CatatanKetelitian>{catatanPetaLobus(ketelitian)}</CatatanKetelitian>
      </section>

      {/* §2 Rasional vs Emosional */}
      <section className="bg-white border border-slate-200 rounded-lg p-6 space-y-3">
        <h2 className="text-lg font-semibold text-slate-900">Rasional vs Emosional</h2>
        <div className="flex h-9 rounded overflow-hidden text-xs font-medium text-white">
          <div className="bg-sky-700 flex items-center justify-center" style={{ width: `${hasil.rasional}%` }}>
            Rasional {fmt(hasil.rasional, 1)}%
          </div>
          <div className="bg-amber-600 flex items-center justify-center" style={{ width: `${hasil.emosional}%` }}>
            Emosional {fmt(hasil.emosional, 1)}%
          </div>
        </div>
        <p className="text-sm">
          Kesimpulan: <b>{hasil.label_belah}</b>
        </p>
        <CatatanKetelitian>{catatanRasional(ketelitian)}</CatatanKetelitian>
      </section>

      {/* §3 Kapasitas */}
      <section className="bg-white border border-slate-200 rounded-lg p-6 space-y-3">
        <h2 className="text-lg font-semibold text-slate-900">9 Kapasitas Mental</h2>
        <p className="text-xs text-slate-500">Satuan TNGF · diurut menurun</p>
        {kapSorted.map((nama) => (
          <KapBar key={nama} nama={nama} item={hasil.kapasitas[nama]} />
        ))}
        <CatatanKetelitian>{catatanKapasitas(ketelitian)}</CatatanKetelitian>
      </section>

      {/* §4 Kecerdasan — pengecualian sadar: "Kecerdasan" = nama bagian baku laporan */}
      <section className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
        <h2 className="text-lg font-semibold text-slate-900">8 Macam Kecerdasan</h2>
        <p className="text-sm text-slate-600" data-ketelitian="">{catatanKecerdasan(ketelitian, namaDepan ?? "anak")}</p>
        {(["menonjol", "tengah", "kurang menonjol"] as const).map((grp) =>
          miByGroup[grp].length > 0 ? (
            <div key={grp}>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-2">{grp}</p>
              {miByGroup[grp].map((nama) => (
                <MiRow
                  key={nama}
                  nama={nama}
                  item={hasil.kecerdasan[nama as keyof HasilZaka["kecerdasan"]]}
                />
              ))}
            </div>
          ) : null,
        )}
      </section>

      {/* §5 Gaya belajar */}
      <section className="bg-white border border-slate-200 rounded-lg p-6 grid grid-cols-3 gap-4 text-center">
        <h2 className="col-span-3 text-lg font-semibold text-slate-900 text-left">Gaya Belajar</h2>
        {(["Visual", "Auditory", "Touch"] as const).map((g) => (
          <div key={g}>
            <p className="text-2xl font-semibold tabular-nums">{fmt(hasil.gaya_belajar[g], 1)}%</p>
            <p className="text-sm text-slate-600">{g}</p>
          </div>
        ))}
        <div className="col-span-3 text-left">
          <CatatanKetelitian>{catatanGayaBelajar(ketelitian)}</CatatanKetelitian>
        </div>
      </section>

      {/* §6 Gaya berfikir */}
      <section className="bg-white border border-slate-200 rounded-lg p-6 space-y-3">
        <h2 className="text-lg font-semibold text-slate-900">Gaya Berfikir</h2>
        <div className="flex h-8 rounded overflow-hidden text-xs text-white font-medium">
          {(["Horizontal", "Vertical", "Abstract"] as const).map((g) => (
            <div
              key={g}
              className={`flex items-center justify-center ${g === hasil.gaya_berfikir_menang ? "bg-emerald-800" : "bg-slate-500"}`}
              style={{ width: `${hasil.gaya_berfikir[g]}%` }}
            >
              {g} {fmt(hasil.gaya_berfikir[g], 1)}%
            </div>
          ))}
        </div>
        <CatatanKetelitian>{catatanGayaBerfikir(ketelitian)}</CatatanKetelitian>
      </section>

      {/* §7 Karakter */}
      <section className="bg-white border border-dashed border-slate-300 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-slate-900">Karakter</h2>
        <p className="text-sm mt-2 text-slate-600">
          Masih ditinjau. Cara menentukannya belum terbukti benar saat diuji pada data yang sudah ada,
          jadi kami memilih tidak menampilkannya sama sekali daripada menampilkan yang belum pasti.
          Akan muncul otomatis kalau sudah selesai — Anda tidak perlu melakukan apa pun.
        </p>
      </section>

      {/* §8 ATD */}
      <section className="bg-white border border-dashed border-slate-300 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-slate-900">Sudut ATD</h2>
        <p className="text-sm mt-2 text-slate-600">
          Belum diukur — pengambilan data belum mencakup foto telapak tangan. ATD tidak bisa dihitung
          dari sidik jari.
        </p>
        <button type="button" className="mt-3 text-sm px-3 py-1.5 rounded bg-slate-800 text-white">
          Jadwalkan pengambilan telapak
        </button>
      </section>

      {/* §9 Data Sumber */}
      {jariData && jariData.length === 10 && (
        <DataSumber hasil={hasil} jari={jariData} ketelitian={ketelitian} />
      )}

      {/* §10 Penutup */}
      <footer className="bg-white border border-slate-200 rounded-lg p-6 text-sm space-y-3">
        <h2 className="text-lg font-semibold text-slate-900">Apa arti semua ini</h2>
        <p>
          Pemetaan ini menunjukkan <b>kecenderungan bawaan</b> — bukan batas kemampuan, bukan ramalan,
          bukan diagnosis. Bandingkan {namaDepan} dengan dirinya sendiri, bukan dengan anak
          lain. Peta ini titik awal percakapan, bukan kesimpulannya.
        </p>
        <p>
          Kalau ada bagian yang tidak terasa seperti anak Anda — kalau Anda membacanya dan berpikir
          &quot;kok bukan anak saya ya&quot; — percaya yang Anda lihat sendiri. Anda mengenalnya setiap hari;
          halaman ini membacanya sekali.
        </p>
        <button type="button" className="text-sm px-4 py-2 rounded bg-emerald-700 text-white">
          Konsultasi dengan konselor
        </button>
      </footer>
    </div>
  );
}
